import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getSummary, getMonthlyReport, getProperties, getGuestLeaveReport, getOutstandingReport, exportExpensesReport, exportOutstandingReport, exportMonthlySummary, exportGuestLeaveReport, getGuestLedgerRegister, exportGuestLedgerReport, processDepositReturn, updateGuest, recordPayment, getGuestStatementList, getGuestStatement, exportGuestStatementExcel, exportGuestStatementPdf, generateInvoice, generateReceipt, getReceiptsPayments, exportReceiptsPaymentsExcel, exportReceiptsPaymentsPdf, getLedgerAudit } from '../lib/api';
import { getISTDateString } from '../lib/dateUtils';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Building, BedDouble, Users, TrendingUp, TrendingDown, Wallet, CreditCard,
  CheckCircle, AlertCircle, Clock, LogOut, AlertTriangle, Download, Phone,
  BookOpen, Banknote, HandCoins, IndianRupee, Search, ShieldCheck
} from 'lucide-react';

// Helper to format due date as ordinal (1st, 2nd, 3rd, 4th, etc.) - matching Guest List format
const formatDueDay = (dateOrDay) => {
  if (!dateOrDay) return '-';
  
  // If it's a full date string (YYYY-MM-DD), extract the day
  let day;
  if (typeof dateOrDay === 'string' && dateOrDay.includes('-')) {
    const parts = dateOrDay.split('-');
    day = parseInt(parts[2], 10);
  } else {
    day = parseInt(dateOrDay, 10);
  }
  
  if (isNaN(day)) return '-';
  
  // Add ordinal suffix
  const suffix = (day === 1 || day === 21 || day === 31) ? 'st' :
                 (day === 2 || day === 22) ? 'nd' :
                 (day === 3 || day === 23) ? 'rd' : 'th';
  
  return `${day}${suffix}`;
};

// Indian comma formatting for the Final Statement
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ label, value, indent }) => (
  <div className={`flex justify-between ${indent ? 'pl-4' : ''} text-slate-600`}>
    <span className="truncate pr-2">{label}</span>
    <span className="font-mono tabular-nums shrink-0">{fmtINR(value)}</span>
  </div>
);

export const Reports = () => {
  const { canEdit } = useAuth();
  const [summary, setSummary] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [guestLeaveReport, setGuestLeaveReport] = useState([]);
  const [outstandingReport, setOutstandingReport] = useState({ data: [], totals: {} });
  const [ledgerRegister, setLedgerRegister] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'outstanding');
  const [loading, setLoading] = useState(true);
  
  // Deposit Return Dialog State
  const [depositReturnDialogOpen, setDepositReturnDialogOpen] = useState(false);
  const [selectedGuestForReturn, setSelectedGuestForReturn] = useState(null);
  const [depositReturnForm, setDepositReturnForm] = useState({
    date: getISTDateString(),
    amount: 0,
    payment_mode: 'cash',
    remarks: ''
  });
  const [processingReturn, setProcessingReturn] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Settle Dues Dialog State (for Ledger)
  const [settleDuesDialogOpen, setSettleDuesDialogOpen] = useState(false);
  const [settleGuestData, setSettleGuestData] = useState(null);
  const [settleDuesRemarks, setSettleDuesRemarks] = useState('');

  // Receive Payment Dialog State (for Ledger - guests who owe us)
  const [receivePaymentDialogOpen, setReceivePaymentDialogOpen] = useState(false);
  const [receiveGuestData, setReceiveGuestData] = useState(null);
  const [receivePaymentForm, setReceivePaymentForm] = useState({
    date: getISTDateString(),
    payment_mode: 'cash',
    amount: 0,
    remarks: ''
  });
  const [processingReceive, setProcessingReceive] = useState(false);

  // Guest Statement State
  const [guestStatementList, setGuestStatementList] = useState([]);
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [guestStatement, setGuestStatement] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [statementStatusFilter, setStatementStatusFilter] = useState('all');
  const [outstandingSearch, setOutstandingSearch] = useState('');
  const [ledgerSearch, setLedgerSearch] = useState('');
  // Ledger Integrity Audit state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [leaveSearch, setLeaveSearch] = useState('');

  // Final Statement (Receipts & Payments) State
  const _nowFy = (() => { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; })();
  const [fsPeriod, setFsPeriod] = useState('monthly');
  const [fsMonth, setFsMonth] = useState(new Date().toISOString().slice(0, 7));
  const [fsYear, setFsYear] = useState(_nowFy);
  const [fsData, setFsData] = useState(null);
  const [fsLoading, setFsLoading] = useState(false);
  const [fsExporting, setFsExporting] = useState(false);

  const fetchFinalStatement = async () => {
    try {
      setFsLoading(true);
      const params = { period: fsPeriod };
      if (fsPeriod === 'monthly') params.month = fsMonth;
      if (fsPeriod === 'yearly') params.year = fsYear;
      const res = await getReceiptsPayments(params);
      setFsData(res.data);
    } catch (err) {
      toast.error('Failed to load Final Statement');
      setFsData(null);
    } finally {
      setFsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'final-statement') fetchFinalStatement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fsPeriod, fsMonth, fsYear]);

  const handleExportFinalStatement = async (kind) => {
    try {
      setFsExporting(true);
      const params = { period: fsPeriod };
      if (fsPeriod === 'monthly') params.month = fsMonth;
      if (fsPeriod === 'yearly') params.year = fsYear;
      const response = kind === 'pdf'
        ? await exportReceiptsPaymentsPdf(params)
        : await exportReceiptsPaymentsExcel(params);
      const label = (fsData?.period_label || 'statement').replace(/[\s/]+/g, '_');
      downloadFile(response.data, `Receipts_Payments_${label}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`);
      toast.success(`Final Statement ${kind.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(`Failed to export Final Statement ${kind.toUpperCase()}`);
    } finally {
      setFsExporting(false);
    }
  };

  // Handle Receive Payment (from Ledger - guest owes us)
  const handleReceivePayment = async () => {
    if (!receiveGuestData) return;
    
    try {
      setProcessingReceive(true);
      
      const paymentData = {
        guest_id: receiveGuestData.guest_id,
        amount: parseFloat(receivePaymentForm.amount),
        payment_type: 'rent',  // Fixed to rent
        payment_mode: receivePaymentForm.payment_mode,
        payment_date: receivePaymentForm.date,
        remarks: receivePaymentForm.remarks || `PAYMENT RECEIVED AGAINST OUTSTANDING DUES`
      };
      
      await recordPayment(paymentData);
      toast.success(`₹${paymentData.amount.toLocaleString()} received from ${receiveGuestData.guest_name}`);
      
      setReceivePaymentDialogOpen(false);
      setReceiveGuestData(null);
      setReceivePaymentForm({
        date: getISTDateString(),
        payment_mode: 'cash',
        amount: 0,
        remarks: ''
      });
      
      // Refresh ledger
      fetchLedgerRegister();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessingReceive(false);
    }
  };

  // Handle Settle Dues (from Ledger - simply write off net balance)
  const handleSettleDues = async () => {
    if (!settleGuestData) return;
    
    try {
      const netBalance = settleGuestData.net_balance || 0;
      const now = getISTDateString();
      
      // Net balance is ALREADY calculated after deposit adjustment
      // Settle simply writes off this amount (receivable or payable)
      const settlementNotes = settleDuesRemarks || 
        (netBalance < 0 
          ? `Receivable of ₹${Math.abs(netBalance).toLocaleString()} written off on ${now}`
          : `Payable of ₹${netBalance.toLocaleString()} written off on ${now}`);
      
      // Update guest - zero out everything and mark as settled
      const updateData = {
        // Zero out all dues
        advance_rent_due: 0,
        rent_due: 0,
        deposit_due: 0,
        electricity_due: 0,
        // Zero out credits
        deposit_paid: 0,
        advance_rent_balance: 0,
        deposit_returned: 0,
        deposit_adjusted: 0,
        // Set status to left if not already
        status: 'left',
        // Only set leave date if guest doesn't already have one
        ...(settleGuestData.status !== 'left' && { actual_leave_date: now }),
        // Settlement tracking fields
        settlement_type: 'write_off',
        settlement_status: 'fully_settled',
        settlement_notes: settlementNotes,
        dues_written_off: Math.abs(netBalance),
        dues_written_off_date: now
      };
      
      await updateGuest(settleGuestData.guest_id, updateData);
      toast.success(`Settled: ₹${Math.abs(netBalance).toLocaleString()} written off for ${settleGuestData.guest_name}`);
      setSettleDuesDialogOpen(false);
      setSettleGuestData(null);
      setSettleDuesRemarks('');
      
      // Refresh ledger
      fetchLedgerRegister();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle dues');
    }
  };

  // Fetch Ledger Register
  const fetchLedgerRegister = async () => {
    try {
      const res = await getGuestLedgerRegister(selectedProperty !== 'all' ? selectedProperty : undefined);
      setLedgerRegister(res.data);
    } catch (err) {
      console.error('Failed to fetch ledger register');
    }
  };

  // Download file helper
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Export handlers
  const handleExportExpenses = async () => {
    try {
      setExporting(true);
      const response = await exportExpensesReport(selectedMonth);
      downloadFile(response.data, `Payments_Report_${selectedMonth}.xlsx`);
      toast.success('Payments Report downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Payments Report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportOutstanding = async () => {
    try {
      setExporting(true);
      const response = await exportOutstandingReport();
      downloadFile(response.data, `Outstanding_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Outstanding Report downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Outstanding Report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportGuestLeave = async () => {
    try {
      setExporting(true);
      const response = await exportGuestLeaveReport();
      downloadFile(response.data, `Guest_Leave_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Guest Leave Report downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Guest Leave Report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMonthlySummary = async () => {
    try {
      setExporting(true);
      const response = await exportMonthlySummary(selectedMonth);
      downloadFile(response.data, `Monthly_Summary_${selectedMonth}.xlsx`);
      toast.success('Monthly Summary downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Monthly Summary');
    } finally {
      setExporting(false);
    }
  };

  const handleExportLedger = async () => {
    try {
      setExporting(true);
      const response = await exportGuestLedgerReport();
      downloadFile(response.data, `Guest_Ledger_Register_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Guest Ledger Register downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Guest Ledger Register');
    } finally {
      setExporting(false);
    }
  };

  const handleRunAudit = async () => {
    try {
      setAuditLoading(true);
      setAuditOpen(true);
      setAuditResult(null);
      const res = await getLedgerAudit();
      setAuditResult(res.data);
    } catch (err) {
      toast.error('Failed to run ledger integrity audit');
      setAuditOpen(false);
    } finally {
      setAuditLoading(false);
    }
  };


  // Guest Statement Handlers
  const fetchGuestStatementList = async () => {
    try {
      const res = await getGuestStatementList(
        selectedProperty !== 'all' ? selectedProperty : undefined,
        statementStatusFilter !== 'all' ? statementStatusFilter : undefined
      );
      setGuestStatementList(res.data || []);
    } catch (err) {
      console.error('Failed to fetch guest list for statement');
    }
  };

  const fetchGuestStatement = async (guestId) => {
    if (!guestId) {
      setGuestStatement(null);
      return;
    }
    try {
      setLoadingStatement(true);
      const res = await getGuestStatement(guestId);
      setGuestStatement(res.data);
    } catch (err) {
      toast.error('Failed to fetch guest statement');
      setGuestStatement(null);
    } finally {
      setLoadingStatement(false);
    }
  };

  const handleExportStatementExcel = async () => {
    if (!selectedGuestId) return;
    try {
      setExporting(true);
      const response = await exportGuestStatementExcel(selectedGuestId);
      const guestName = guestStatement?.guest_name || 'Guest';
      downloadFile(response.data, `Guest_Statement_${guestName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Guest Statement Excel downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Guest Statement Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportStatementPdf = async () => {
    if (!selectedGuestId) return;
    try {
      setExporting(true);
      const response = await exportGuestStatementPdf(selectedGuestId);
      const guestName = guestStatement?.guest_name || 'Guest';
      downloadFile(response.data, `Guest_Statement_${guestName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Guest Statement PDF downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Guest Statement PDF');
    } finally {
      setExporting(false);
    }
  };

  // Invoice/Receipt Generation Handlers
  const [generatingDoc, setGeneratingDoc] = useState(false);

  const handleGenerateInvoice = async (stmt) => {
    if (!selectedGuestId || !stmt) return;
    try {
      setGeneratingDoc(true);
      const response = await generateInvoice({
        guest_id: selectedGuestId,
        entry_type: stmt.type,
        entry_date: stmt.date,
        amount: stmt.debit,
        description: stmt.particulars
      });
      const guestName = guestStatement?.guest_name || 'Guest';
      downloadFile(response.data, `Invoice_${guestName.replace(/\s+/g, '_')}_${stmt.date}.pdf`);
      toast.success('Invoice generated successfully');
    } catch (err) {
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingDoc(false);
    }
  };

  const handleGenerateReceipt = async (stmt) => {
    if (!selectedGuestId || !stmt) return;
    try {
      setGeneratingDoc(true);
      const response = await generateReceipt({
        guest_id: selectedGuestId,
        payment_id: stmt.payment_id || null,
        entry_date: stmt.date,
        amount: stmt.credit,
        payment_type: stmt.payment_type || 'rent',
        payment_mode: stmt.payment_mode || 'cash',
        description: stmt.particulars
      });
      const guestName = guestStatement?.guest_name || 'Guest';
      downloadFile(response.data, `Receipt_${guestName.replace(/\s+/g, '_')}_${stmt.date}.pdf`);
      toast.success('Receipt generated successfully');
    } catch (err) {
      toast.error('Failed to generate receipt');
    } finally {
      setGeneratingDoc(false);
    }
  };

  // Open deposit return dialog
  const openDepositReturnDialog = (guest) => {
    const netBalance = guest.net_balance || guest.net_ledger_balance || 0;
    setSelectedGuestForReturn(guest);
    setDepositReturnForm({
      date: getISTDateString(),
      amount: Math.abs(netBalance),
      payment_mode: 'cash',
      remarks: ''
    });
    setDepositReturnDialogOpen(true);
  };

  // Handle deposit return submission
  const handleDepositReturn = async () => {
    if (!selectedGuestForReturn) return;
    
    try {
      setProcessingReturn(true);
      await processDepositReturn({
        guest_id: selectedGuestForReturn.guest_id,
        date: depositReturnForm.date,
        amount: depositReturnForm.amount,
        payment_mode: depositReturnForm.payment_mode,
        remarks: depositReturnForm.remarks
      });
      
      toast.success(`₹${depositReturnForm.amount.toLocaleString()} deposit returned to ${selectedGuestForReturn.name}`);
      setDepositReturnDialogOpen(false);
      setSelectedGuestForReturn(null);
      
      // Refresh data
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process deposit return');
    } finally {
      setProcessingReturn(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedProperty, selectedMonth, activeTab]);

  // Refetch guest statement list when filter changes
  useEffect(() => {
    if (activeTab === 'guest-statement') {
      fetchGuestStatementList();
    }
  }, [selectedProperty, statementStatusFilter, activeTab]);

  // Fetch statement when guest is selected
  useEffect(() => {
    if (selectedGuestId) {
      fetchGuestStatement(selectedGuestId);
    }
  }, [selectedGuestId]);

  const fetchData = async () => {
    try {
      const [summaryRes, monthlyRes, propsRes] = await Promise.all([
        getSummary(selectedProperty === 'all' ? null : selectedProperty),
        getMonthlyReport(selectedMonth),
        getProperties()
      ]);
      setSummary(summaryRes.data);
      setMonthlyReport(monthlyRes.data);
      setProperties(propsRes.data);

      // Fetch additional reports based on active tab
      if (activeTab === 'guest-leave') {
        const res = await getGuestLeaveReport(selectedMonth);
        setGuestLeaveReport(res.data || []);
      } else if (activeTab === 'outstanding') {
        const res = await getOutstandingReport();
        setOutstandingReport(res.data);
      } else if (activeTab === 'ledger') {
        const res = await getGuestLedgerRegister();
        setLedgerRegister(res.data || []);
      } else if (activeTab === 'guest-statement') {
        await fetchGuestStatementList();
      }
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  const _q = (s) => (s || '').toString().toLowerCase();
  const filteredOutstanding = (outstandingReport.data || []).filter(i => {
    const s = _q(outstandingSearch);
    return !s || _q(i.guest_name).includes(s) || _q(i.room).includes(s) || _q(i.mobile).includes(s);
  });
  const filteredLedger = (ledgerRegister || []).filter(g => {
    const s = _q(ledgerSearch);
    return !s || _q(g.name || g.name_of_guest).includes(s) || _q(g.room || g.flat_no).includes(s) || _q(g.mobile || g.mobile_number).includes(s) || _q(g.guest_code).includes(s);
  });
  const filteredLeave = (guestLeaveReport || []).filter(g => {
    const s = _q(leaveSearch);
    return !s || _q(g.guest_name).includes(s) || _q(g.room_info).includes(s) || _q(g.mobile).includes(s) || _q(g.guest_code).includes(s);
  });

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Reports</h1>
          <p className="text-slate-500 mt-1">Summary, receipts, and analytics</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="report-property-filter">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {activeTab === 'guest-leave' && (
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full sm:w-[180px]"
              data-testid="report-month-filter"
            />
          )}
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-5">
          <TabsTrigger value="outstanding" data-testid="outstanding-tab" className="flex-shrink-0">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Outstanding Dues
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="ledger-tab" className="flex-shrink-0">
            <BookOpen className="h-4 w-4 mr-2" />
            Final Settlement
          </TabsTrigger>
          <TabsTrigger value="guest-statement" data-testid="guest-statement-tab" className="flex-shrink-0">
            <Wallet className="h-4 w-4 mr-2" />
            Guest Statement
          </TabsTrigger>
          <TabsTrigger value="final-statement" data-testid="final-statement-tab" className="flex-shrink-0">
            <Banknote className="h-4 w-4 mr-2" />
            Final Statement
          </TabsTrigger>
          <TabsTrigger value="guest-leave" data-testid="guest-leave-tab" className="flex-shrink-0">
            <LogOut className="h-4 w-4 mr-2" />
            Guest Leave
          </TabsTrigger>
        </TabsList>

        {/* Guest Leave Report Tab */}
        <TabsContent value="guest-leave" className="space-y-6">
          {/* Download Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleExportGuestLeave} 
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="export-guest-leave-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Download Excel'}
            </Button>
          </div>

          {/* Summary Card */}
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-200 rounded-full">
                  <CheckCircle className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-800">
                    {guestLeaveReport.length} Guest{guestLeaveReport.length !== 1 ? 's' : ''} Fully Checked Out
                  </p>
                  <p className="text-sm text-emerald-600">
                    Only showing guests with status &quot;Left&quot; and net balance ₹0 (fully settled)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="relative w-full sm:w-[280px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={leaveSearch} onChange={(e) => setLeaveSearch(e.target.value)} placeholder="Search guest, room or mobile..." className="pl-9" data-testid="leave-search" />
          </div>

          {/* Guest Leave Table */}
          <Card className="dash-animate relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 z-10" />
            <CardHeader>
              <CardTitle className="text-lg">Guest Leave Records</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="whitespace-nowrap">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Guest ID</TableHead>
                      <TableHead>Guest Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Leave Date</TableHead>
                      <TableHead className="text-center">Stay Duration</TableHead>
                      <TableHead className="text-right">Monthly Rent</TableHead>
                      <TableHead className="text-right">Total Paid</TableHead>
                      <TableHead className="text-right">Deposit Return</TableHead>
                      <TableHead className="text-center">Settlement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {guestLeaveReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                          No fully settled guests found. Guests appear here only after checkout with ₹0 balance.
                        </TableCell>
                      </TableRow>
                    ) : filteredLeave.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-10 text-slate-500">
                          <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                          <p>No guests match your search</p>
                        </TableCell>
                      </TableRow>
                    ) : filteredLeave.map((guest, idx) => (
                      <TableRow key={idx} className="transition-colors hover:bg-slate-50">
                        <TableCell><span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 whitespace-nowrap inline-block">{guest.guest_code || '—'}</span></TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{guest.guest_name}</p>
                            {guest.aadhar && (
                              <p className="text-xs text-slate-500">Aadhar: {guest.aadhar}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <a href={`tel:${guest.mobile}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {guest.mobile}
                          </a>
                          {guest.emergency_contact && (
                            <p className="text-xs text-slate-500 mt-1">
                              Emergency: {guest.emergency_contact}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{guest.property}</TableCell>
                        <TableCell className="font-mono">{guest.room_info}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(guest.joining_date)}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(guest.actual_leave_date)}</TableCell>
                        <TableCell className="text-center">
                          <div>
                            <span className="font-mono font-medium">{guest.stay_days}</span>
                            <span className="text-slate-500 text-xs"> days</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            ({guest.stay_months} months)
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">₹{(guest.monthly_rent || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono font-medium">₹{(guest.total_paid || 0).toLocaleString()}</div>
                          <div className="text-xs text-slate-500">
                            R: ₹{(guest.total_rent_paid || 0).toLocaleString()} | D: ₹{(guest.deposit_paid || 0).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono">₹{(guest.deposit_returned || 0).toLocaleString()}</div>
                          {guest.deposit_adjusted > 0 ? (
                            <div className="text-xs text-amber-600">
                              Adj: ₹{(guest.deposit_adjusted || 0).toLocaleString()}
                            </div>
                          ) : (
                            <div className={`text-xs ${guest.final_deposit_status === 'returned' ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {guest.final_deposit_status === 'returned' ? 'Full Return' : 'Adjusted'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <CheckCircle className="h-3 w-3" />
                            Settled
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outstanding Dues Report Tab */}
        <TabsContent value="outstanding" className="space-y-6">
          {/* Download Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleExportOutstanding} 
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="export-outstanding-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Download Excel'}
            </Button>
          </div>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
            <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '0ms' }}>
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 opacity-10"></div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Deposit Due</p>
              <p className="text-2xl font-bold font-mono text-rose-700 mt-0.5" data-testid="deposit-due-amount">₹{(outstandingReport.totals?.total_deposit_due || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="deposit-due-count">{outstandingReport.totals?.deposit_due_count || 0} Guests</p>
            </div>
            <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-amber-200" style={{ animationDelay: '60ms' }}>
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 opacity-10"></div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Rent Due</p>
              <p className="text-2xl font-bold font-mono text-amber-700 mt-0.5" data-testid="rent-due-amount">₹{(outstandingReport.totals?.total_rent_due || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="rent-due-count">{outstandingReport.totals?.rent_due_count || 0} Guests</p>
            </div>
            <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-blue-200" style={{ animationDelay: '120ms' }}>
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-10"></div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Electricity Due</p>
              <p className="text-2xl font-bold font-mono text-blue-700 mt-0.5" data-testid="electricity-due-amount">₹{(outstandingReport.totals?.total_electricity_due || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="electricity-due-count">{outstandingReport.totals?.electricity_due_count || 0} Guests</p>
            </div>
            <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '160ms' }}>
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-red-600 opacity-10"></div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Penalty Due</p>
              <p className="text-2xl font-bold font-mono text-rose-700 mt-0.5" data-testid="penalty-due-amount">₹{(outstandingReport.totals?.total_penalty_due || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="penalty-due-count">{outstandingReport.totals?.penalty_due_count || 0} Guests</p>
            </div>
            <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-purple-200" style={{ animationDelay: '180ms' }}>
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 opacity-10"></div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Grand Total</p>
              <p className="text-2xl font-bold font-mono text-purple-700 mt-0.5" data-testid="grand-total-amount">₹{(outstandingReport.totals?.grand_total_due || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="total-guests-count">{outstandingReport.totals?.total_guests_count || 0} Guests</p>
            </div>
          </div>

          <div className="relative w-full sm:w-[280px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={outstandingSearch} onChange={(e) => setOutstandingSearch(e.target.value)} placeholder="Search guest, room or mobile..." className="pl-9" data-testid="outstanding-search" />
          </div>

          <Card className="dash-animate relative overflow-hidden" style={{ animationDelay: '220ms' }}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-pink-600 z-10" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Outstanding Dues - Track & Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="whitespace-nowrap">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Guest ID</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Sharing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Joining</TableHead>
                      <TableHead className="text-right">Deposit (₹) Dues</TableHead>
                      <TableHead className="text-right">Rent (₹) Dues</TableHead>
                      <TableHead className="text-right">Electricity (₹) Dues</TableHead>
                      <TableHead className="text-right">Penalty (₹) Dues</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-center">Days Delayed</TableHead>
                      <TableHead className="text-right">Total Due</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(outstandingReport.data || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center py-10 text-slate-500">
                          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-300" />
                          <p>No outstanding dues - All guests are up to date!</p>
                        </TableCell>
                      </TableRow>
                    ) : filteredOutstanding.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center py-10 text-slate-500">
                          <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                          <p>No dues match your search</p>
                        </TableCell>
                      </TableRow>
                    ) : filteredOutstanding.map((item, idx) => (
                      <TableRow key={idx} className={`transition-colors ${item.days_delayed > 0 ? 'bg-rose-50/50 hover:bg-rose-100/60' : 'hover:bg-slate-50'}`}>
                        <TableCell><span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 whitespace-nowrap inline-block">{item.guest_code || '—'}</span></TableCell>
                        <TableCell className="font-mono">{item.room}</TableCell>
                        <TableCell className="text-xs">{item.sharing_type}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'booked' ? 'bg-blue-100 text-blue-700' :
                            item.status === 'new_join' ? 'bg-emerald-100 text-emerald-700' :
                            item.status === 'occupied' ? 'bg-slate-100 text-slate-700' :
                            item.status === 'notice' ? 'bg-amber-100 text-amber-700' :
                            item.status === 'shifting' ? 'bg-purple-100 text-purple-700' :
                            item.status === 'left' ? 'bg-rose-100 text-rose-700' :
                            item.status === 'joined' ? 'bg-emerald-100 text-emerald-700' :
                            item.status === 'day_basis' ? 'bg-purple-100 text-purple-700' :
                            item.status === 'overstay' ? 'bg-red-500 text-white' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {item.status === 'booked' ? 'Booked' :
                             item.status === 'new_join' ? 'Joined' :
                             item.status === 'occupied' ? 'Occupied' :
                             item.status === 'notice' ? 'Notice' :
                             item.status === 'shifting' ? 'Shifting' :
                             item.status === 'left' ? 'Left' :
                             item.status === 'joined' ? 'Joined' :
                             item.status === 'day_basis' ? 'Day Basis' :
                             item.status === 'overstay' ? 'Overstay' :
                             item.status}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{item.guest_name}</TableCell>
                        <TableCell>
                          <a href={`tel:${item.mobile}`} className="text-blue-600 hover:underline flex items-center gap-1 font-mono text-sm">
                            <Phone className="h-3 w-3" />
                            {item.mobile}
                          </a>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(item.joining_date)}</TableCell>
                        <TableCell className="text-right font-mono text-rose-600 font-medium">
                          {item.deposit_due > 0 ? `₹${item.deposit_due.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-600 font-medium">
                          {item.rent_due > 0 ? `₹${item.rent_due.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600 font-medium">
                          {item.electricity_due > 0 ? `₹${item.electricity_due.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-600 font-medium">
                          {item.penalty_due > 0 ? `₹${item.penalty_due.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                            {formatDueDay(item.due_date)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.days_delayed > 0 ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              item.days_delayed > 30 ? 'bg-red-100 text-red-700' :
                              item.days_delayed > 15 ? 'bg-orange-100 text-orange-700' :
                              item.days_delayed > 7 ? 'bg-amber-100 text-amber-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {item.days_delayed} days
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-purple-700">
                          ₹{(item.total_due || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Summary Row */}
                    {filteredOutstanding.length > 0 && (
                      <TableRow className="bg-slate-100 font-bold">
                        <TableCell colSpan={7} className="text-right">TOTAL</TableCell>
                        <TableCell className="text-right font-mono text-rose-700">
                          ₹{(outstandingReport.totals?.total_deposit_due || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-700">
                          ₹{(outstandingReport.totals?.total_rent_due || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-700">
                          ₹{(outstandingReport.totals?.total_electricity_due || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-700">
                          ₹{(outstandingReport.totals?.total_penalty_due || 0).toLocaleString()}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className="text-right font-mono text-purple-700 text-lg">
                          ₹{(outstandingReport.totals?.grand_total_due || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest Ledger Register Tab */}
        <TabsContent value="ledger" className="space-y-6">
          {/* Download Button */}
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleRunAudit}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="ledger-audit-btn"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Integrity Audit
            </Button>
            <Button 
              onClick={handleExportLedger} 
              disabled={exporting}
              className="!bg-emerald-600 hover:!bg-emerald-700 text-white"
              data-testid="export-ledger-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Download Excel'}
            </Button>
          </div>
          
          <div className="relative w-full sm:w-[280px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)} placeholder="Search guest, room or mobile..." className="pl-9" data-testid="ledger-search" />
          </div>

          {/* Ledger Integrity Audit Dialog */}
          <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
            <DialogContent className="max-w-2xl" data-testid="ledger-audit-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-amber-600" />
                  Ledger Integrity Audit
                </DialogTitle>
              </DialogHeader>
              {auditLoading ? (
                <div className="py-10 text-center text-slate-500" data-testid="audit-loading">Scanning all guests…</div>
              ) : auditResult ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Checked {auditResult.total_guests} guest(s) at {formatDateTime(auditResult.checked_at, true)}.
                  </p>
                  {auditResult.flagged_count === 0 ? (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 flex items-center gap-2" data-testid="audit-clean">
                      <CheckCircle className="h-5 w-5" />
                      <span>All clear — no advance/due conflicts found. Ledger and statements are consistent.</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-rose-800 text-sm font-medium" data-testid="audit-flagged-count">
                        {auditResult.flagged_count} guest(s) need attention (an advance and a due exist together — logically impossible).
                      </div>
                      <div className="max-h-[50vh] overflow-y-auto space-y-2">
                        {auditResult.flagged.map((g) => (
                          <div key={g.guest_id} className="border rounded-lg p-3" data-testid={`audit-row-${g.guest_id}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-slate-800">{g.name}</p>
                                <p className="text-xs text-slate-500">Room {g.room} · {g.status}</p>
                              </div>
                              <span className="text-sm font-mono">₹{(g.net_balance || 0).toLocaleString()} {g.balance_type}</span>
                            </div>
                            <ul className="mt-2 text-xs text-rose-700 list-disc pl-5 space-y-0.5">
                              {g.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </DialogContent>
          </Dialog>

          <Card className="dash-animate relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 z-10" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Final Settlement
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead>Guest ID</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Sharing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Joining</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Deposit Paid</TableHead>
                      <TableHead className="text-right">Advance</TableHead>
                      <TableHead className="text-right">Total Outstanding</TableHead>
                      <TableHead className="text-right">Net Balance</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerRegister.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-10 text-slate-500">
                          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No guest data available</p>
                        </TableCell>
                      </TableRow>
                    ) : filteredLedger.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-10 text-slate-500">
                          <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                          <p>No guests match your search</p>
                        </TableCell>
                      </TableRow>
                    ) : filteredLedger.map((guest, idx) => {
                      const netBal = guest.net_balance || guest.net_ledger_balance || 0;
                      // Positive = Payable (green), Negative = Receivable (red)
                      const rowClass = netBal > 0 ? 'bg-emerald-50/50 hover:bg-emerald-100/60' : netBal < 0 ? 'bg-rose-50/50 hover:bg-rose-100/60' : 'hover:bg-slate-50';
                      const isPayable = netBal > 0 && guest.balance_type === 'Payable';
                      return (
                      <TableRow key={idx} className={`transition-colors ${rowClass}`}>
                        <TableCell>
                          <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 whitespace-nowrap inline-block">{guest.guest_code || '—'}</span>
                        </TableCell>
                        <TableCell className="font-mono">{guest.room || guest.flat_no}</TableCell>
                        <TableCell className="text-xs">{guest.sharing_type || guest.bed_sharing_type}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            guest.status === 'booked' ? 'bg-blue-100 text-blue-700' :
                            guest.status === 'new_join' ? 'bg-emerald-100 text-emerald-700' :
                            guest.status === 'occupied' ? 'bg-slate-100 text-slate-700' :
                            guest.status === 'notice' ? 'bg-amber-100 text-amber-700' :
                            guest.status === 'shifting' ? 'bg-purple-100 text-purple-700' :
                            guest.status === 'left' ? 'bg-rose-100 text-rose-700' :
                            guest.status === 'joined' || guest.status === 'Joined' ? 'bg-emerald-100 text-emerald-700' :
                            guest.status === 'day_basis' ? 'bg-purple-100 text-purple-700' :
                            guest.status === 'overstay' ? 'bg-red-500 text-white' :
                            guest.status === 'Occupied' ? 'bg-slate-100 text-slate-700' :
                            guest.status === 'Booked' ? 'bg-blue-100 text-blue-700' :
                            guest.status === 'Notice' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {guest.status === 'booked' ? 'Booked' :
                             guest.status === 'new_join' ? 'Joined' :
                             guest.status === 'occupied' ? 'Occupied' :
                             guest.status === 'notice' ? 'Notice' :
                             guest.status === 'shifting' ? 'Shifting' :
                             guest.status === 'left' ? 'Left' :
                             guest.status === 'joined' ? 'Joined' :
                             guest.status === 'day_basis' ? 'Day Basis' :
                             guest.status === 'overstay' ? 'Overstay' :
                             guest.status}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{guest.name || guest.name_of_guest}</TableCell>
                        <TableCell>
                          <a href={`tel:${guest.mobile || guest.mobile_number}`} className="text-blue-600 hover:underline flex items-center gap-1 font-mono text-sm">
                            <Phone className="h-3 w-3" />
                            {guest.mobile || guest.mobile_number}
                          </a>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(guest.joining_date)}</TableCell>
                        <TableCell>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                            {formatDueDay(guest.due_date)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600 font-medium">
                          ₹{(guest.deposit_paid || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-purple-600 font-medium">
                          {(guest.advance || 0) > 0 
                            ? `₹${(guest.advance || 0).toLocaleString()}`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-600 font-medium">
                          ₹{(guest.total_outstanding || guest.outstanding || 0).toLocaleString()}
                          {/* For LEFT guests with dues_settled, show what was adjusted from deposit */}
                          {guest.status === 'left' && (guest.dues_settled || 0) > 0 ? (
                            <span className="block text-[9px] text-amber-600 font-normal">
                              (Settled: ₹{(guest.dues_settled || 0).toLocaleString()} from deposit)
                            </span>
                          ) : (
                            <span className="block text-[9px] text-slate-500 font-normal">
                              (D: ₹{(guest.deposit_due || guest.deposit || 0).toLocaleString()} + R: ₹{(guest.rent_due || guest.rent || 0).toLocaleString()} + E: ₹{(guest.electricity_due || guest.electricity || 0).toLocaleString()}{(guest.penalty_due || 0) > 0 ? ` + P: ₹${(guest.penalty_due || 0).toLocaleString()}` : ''})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold ${
                          (guest.net_balance || guest.net_ledger_balance || 0) > 0 ? 'text-emerald-600' : 
                          (guest.net_balance || guest.net_ledger_balance || 0) < 0 ? 'text-rose-600' : 'text-slate-600'
                        }`}>
                          {(guest.net_balance || guest.net_ledger_balance || 0) > 0 
                            ? `₹${(guest.net_balance || guest.net_ledger_balance || 0).toLocaleString()}` 
                            : (guest.net_balance || guest.net_ledger_balance || 0) < 0
                            ? `-₹${Math.abs(guest.net_balance || guest.net_ledger_balance || 0).toLocaleString()}`
                            : '₹0'}
                          <span className={`block text-[10px] font-normal ${
                            guest.balance_type === 'Payable' ? 'text-emerald-500' : 
                            guest.balance_type === 'Receivable' ? 'text-rose-500' : 'text-slate-500'
                          }`}>
                            {guest.balance_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {canEdit('receipts') && (
                          <div className="flex items-center justify-center gap-2 flex-nowrap">
                            {/* Receive button - for guests who owe us (Receivable / negative net balance) */}
                            {(guest.balance_type === 'Receivable' || netBal < 0) && netBal !== 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-rose-300 text-rose-700 hover:bg-rose-50 whitespace-nowrap"
                                onClick={() => {
                                  setReceiveGuestData(guest);
                                  setReceivePaymentForm({
                                    date: getISTDateString(),
                                    payment_mode: 'cash',
                                    amount: Math.abs(netBal),
                                    remarks: ''
                                  });
                                  setReceivePaymentDialogOpen(true);
                                }}
                                data-testid={`receive-payment-btn-${guest.guest_id}`}
                              >
                                <HandCoins className="h-3 w-3 mr-1" />
                                Receive
                              </Button>
                            )}
                            {/* Pay button - for guests we owe (Payable / positive net balance) */}
                            {isPayable && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                                onClick={() => openDepositReturnDialog(guest)}
                                data-testid={`pay-deposit-btn-${guest.guest_id}`}
                              >
                                <Banknote className="h-3 w-3 mr-1" />
                                Pay
                              </Button>
                            )}
                            {/* Settle button - for any guest with non-zero balance */}
                            {netBal !== 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                                onClick={() => {
                                  setSettleGuestData({
                                    guest_id: guest.guest_id || guest.id,
                                    guest_name: guest.name || guest.name_of_guest,
                                    net_balance: netBal,
                                    deposit_paid: guest.deposit_paid || 0,
                                    status: guest.status,
                                    balance_type: guest.balance_type
                                  });
                                  setSettleDuesRemarks('');
                                  setSettleDuesDialogOpen(true);
                                }}
                                data-testid={`settle-dues-btn-${guest.guest_id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Settle
                              </Button>
                            )}
                          </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )})}
                    {/* Summary Row */}
                    {ledgerRegister.length > 0 && (
                      <TableRow className="bg-slate-200 font-bold">
                        <TableCell colSpan={8} className="text-right">TOTAL</TableCell>
                        <TableCell className="text-right font-mono text-emerald-700">
                          ₹{ledgerRegister.reduce((sum, g) => sum + (g.deposit_paid || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-purple-700">
                          ₹{ledgerRegister.reduce((sum, g) => sum + (g.advance || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-700">
                          ₹{ledgerRegister.reduce((sum, g) => sum + (g.total_outstanding || g.outstanding || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-purple-700">
                          ₹{ledgerRegister.reduce((sum, g) => sum + (g.net_balance || g.net_ledger_balance || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest Statement Tab */}
        <TabsContent value="guest-statement" className="space-y-6">
          {/* Filters and Export Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Status Filter */}
              <Select value={statementStatusFilter} onValueChange={setStatementStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="statement-status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="joined">Joined</SelectItem>
                  <SelectItem value="notice">Notice</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Guest Selector */}
              <Select value={selectedGuestId} onValueChange={setSelectedGuestId}>
                <SelectTrigger className="w-[280px]" data-testid="statement-guest-selector">
                  <SelectValue placeholder="Select a guest..." />
                </SelectTrigger>
                <SelectContent>
                  {guestStatementList.length === 0 ? (
                    <SelectItem value="_none" disabled>No guests found</SelectItem>
                  ) : (
                    guestStatementList.map(g => {
                      const statusLabel = g.display_type === 'overstay' 
                        ? `Overstay (${g.overstay_days || 0}d)` 
                        : g.display_type === 'new_join'
                        ? 'Joined'
                        : g.display_type === 'occupied'
                        ? 'Occupied'
                        : (g.display_type || g.status || '').charAt(0).toUpperCase() + (g.display_type || g.status || '').slice(1);
                      return (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name} ({g.room || 'No Room'}) - {statusLabel}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={handleExportStatementExcel} 
                disabled={exporting || !selectedGuestId || !guestStatement}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="export-statement-excel-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Excel'}
              </Button>
              <Button 
                onClick={handleExportStatementPdf} 
                disabled={exporting || !selectedGuestId || !guestStatement}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="export-statement-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'PDF'}
              </Button>
            </div>
          </div>

          {/* Guest Info Card */}
          {guestStatement && (
            <Card className="bg-slate-50">
              <CardContent className="py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Guest ID</p>
                    <p className="font-mono font-semibold text-indigo-700">{guestStatement.guest_code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Guest Name</p>
                    <p className="font-semibold">{guestStatement.guest_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Mobile</p>
                    <p className="font-mono">{guestStatement.mobile}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Property / Room</p>
                    <p className="font-medium">{guestStatement.property} / {guestStatement.room}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      guestStatement.display_type === 'overstay' ? 'bg-red-500 text-white' :
                      guestStatement.display_type === 'new_join' ? 'bg-emerald-100 text-emerald-700' :
                      guestStatement.display_type === 'occupied' ? 'bg-slate-100 text-slate-700' :
                      guestStatement.display_type === 'notice' ? 'bg-amber-100 text-amber-700' :
                      guestStatement.display_type === 'left' ? 'bg-rose-100 text-rose-700' :
                      guestStatement.display_type === 'booked' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {guestStatement.display_type === 'overstay' 
                        ? `Overstay (${guestStatement.overstay_days || 0}d)`
                        : guestStatement.display_type === 'new_join'
                        ? 'Joined'
                        : guestStatement.display_type === 'occupied'
                        ? 'Occupied'
                        : (guestStatement.display_type || guestStatement.status || 'Unknown').charAt(0).toUpperCase() + (guestStatement.display_type || guestStatement.status || 'unknown').slice(1)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Joining Date</p>
                    <p className="font-mono text-sm">{formatDate(guestStatement.joining_date)}</p>
                  </div>
                  {guestStatement.leave_date && (
                    <div>
                      <p className="text-xs text-slate-500">Leave Date</p>
                      <p className="font-mono text-sm">{formatDate(guestStatement.leave_date)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-500">Monthly Rent</p>
                    <p className="font-mono font-medium">₹{(guestStatement.monthly_rent || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Rent Due Day</p>
                    <p className="font-mono font-medium">
                      {guestStatement.rent_due_day === 1 ? '1st' : 
                       guestStatement.rent_due_day === 2 ? '2nd' :
                       guestStatement.rent_due_day === 3 ? '3rd' :
                       `${guestStatement.rent_due_day}th`} of month
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Final Balance</p>
                    <p className={`font-mono font-bold ${
                      guestStatement.final_balance > 0 ? 'text-rose-600' :
                      guestStatement.final_balance < 0 ? 'text-emerald-600' :
                      'text-slate-600'
                    }`}>
                      {guestStatement.final_balance > 0 
                        ? `₹${guestStatement.final_balance.toLocaleString()} (${guestStatement.balance_type})`
                        : guestStatement.final_balance < 0
                        ? `₹${Math.abs(guestStatement.final_balance).toLocaleString()} (${guestStatement.balance_type})`
                        : '₹0 (Settled)'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statement Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Guest Statement
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingStatement ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
                </div>
              ) : !selectedGuestId ? (
                <div className="text-center py-12 text-slate-500">
                  <Wallet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Select a guest to view their financial statement</p>
                </div>
              ) : !guestStatement || guestStatement.statements?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Wallet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No transactions found for this guest</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="whitespace-nowrap">
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="w-[100px]">Date</TableHead>
                        <TableHead>Particulars</TableHead>
                        <TableHead className="text-right">Debit (₹)</TableHead>
                        <TableHead className="text-right">Credit (₹)</TableHead>
                        <TableHead className="text-right">Balance (₹)</TableHead>
                        <TableHead className="text-center">Remarks</TableHead>
                        <TableHead className="text-center w-[100px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {guestStatement.statements.map((stmt, idx) => (
                        <TableRow key={idx} className={
                          stmt.type === 'due' ? '' :
                          stmt.type === 'payment' ? 'bg-emerald-50/50' :
                          stmt.type === 'adjustment' ? 'bg-amber-50/50' :
                          stmt.type === 'return' ? 'bg-blue-50/50' : ''
                        }>
                          <TableCell className="font-mono text-sm">{formatDate(stmt.date)}</TableCell>
                          <TableCell className="font-medium">{stmt.particulars}</TableCell>
                          <TableCell className="text-right font-mono text-rose-600">
                            {stmt.debit > 0 ? `₹${stmt.debit.toLocaleString()}` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">
                            {stmt.credit > 0 ? `₹${stmt.credit.toLocaleString()}` : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-medium ${
                            stmt.balance > 0 ? 'text-rose-600 bg-rose-50' :
                            stmt.balance < 0 ? 'text-emerald-600 bg-emerald-50' :
                            'text-slate-600'
                          }`}>
                            {stmt.balance > 0 
                              ? `₹${stmt.balance.toLocaleString()}`
                              : stmt.balance < 0
                              ? `-₹${Math.abs(stmt.balance).toLocaleString()}`
                              : '₹0'}
                          </TableCell>
                          <TableCell className="text-center text-sm text-slate-500">{stmt.remarks}</TableCell>
                          <TableCell className="text-center">
                            {stmt.debit > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                                onClick={() => handleGenerateInvoice(stmt)}
                                disabled={generatingDoc}
                                data-testid={`invoice-btn-${idx}`}
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Invoice
                              </Button>
                            )}
                            {stmt.credit > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => handleGenerateReceipt(stmt)}
                                disabled={generatingDoc}
                                data-testid={`receipt-btn-${idx}`}
                              >
                                <IndianRupee className="h-3 w-3 mr-1" />
                                Receipt
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Final Balance Row */}
                      <TableRow className="bg-slate-200 font-bold">
                        <TableCell colSpan={4} className="text-right">FINAL BALANCE</TableCell>
                        <TableCell className={`text-right font-mono ${
                          guestStatement.final_balance > 0 ? 'text-rose-700' :
                          guestStatement.final_balance < 0 ? 'text-emerald-700' :
                          'text-slate-700'
                        }`}>
                          {guestStatement.final_balance > 0 
                            ? `₹${guestStatement.final_balance.toLocaleString()}`
                            : guestStatement.final_balance < 0
                            ? `-₹${Math.abs(guestStatement.final_balance).toLocaleString()}`
                            : '₹0'}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {guestStatement.balance_type}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="bg-slate-50">
            <CardContent className="py-3">
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-rose-100 rounded"></div>
                  <span>Debit (Due)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-100 rounded"></div>
                  <span>Credit (Payment)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-amber-100 rounded"></div>
                  <span>Adjustment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-100 rounded"></div>
                  <span>Return</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Final Statement (Receipts & Payments) Tab */}
        <TabsContent value="final-statement" className="space-y-6">
          <Card data-testid="final-statement-card">
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                    Receipts &amp; Payments Statement
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Organisation-wide cash-basis statement (all properties).
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Period</Label>
                    <Select value={fsPeriod} onValueChange={setFsPeriod}>
                      <SelectTrigger className="w-[140px]" data-testid="fs-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly (FY)</SelectItem>
                        <SelectItem value="lifetime">Lifetime</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {fsPeriod === 'monthly' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Month</Label>
                      <Input
                        type="month"
                        value={fsMonth}
                        onChange={(e) => setFsMonth(e.target.value)}
                        className="w-[160px]"
                        data-testid="fs-month-input"
                      />
                    </div>
                  )}
                  {fsPeriod === 'yearly' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Financial Year</Label>
                      <Select value={String(fsYear)} onValueChange={(v) => setFsYear(parseInt(v, 10))}>
                        <SelectTrigger className="w-[150px]" data-testid="fs-year-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 6 }).map((_, i) => {
                            const y = _nowFy - i;
                            return <SelectItem key={y} value={String(y)}>{`${y}-${String((y + 1) % 100).padStart(2, '0')}`}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => handleExportFinalStatement('excel')}
                    disabled={fsExporting || !fsData}
                    data-testid="fs-export-excel-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />Excel
                  </Button>
                  <Button
                    onClick={() => handleExportFinalStatement('pdf')}
                    disabled={fsExporting || !fsData}
                    data-testid="fs-export-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {fsLoading ? (
                <div className="py-16 text-center text-slate-400" data-testid="fs-loading">Loading statement…</div>
              ) : !fsData ? (
                <div className="py-16 text-center text-slate-400">No data available</div>
              ) : (
                <div data-testid="fs-statement">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-bold text-slate-900">{fsData.org_name}</h3>
                    <p className="text-sm text-slate-600">Receipts and Payments</p>
                    <p className="text-xs text-slate-400">{fsData.period_label}</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
                    {/* Receipts side */}
                    <div className="bg-white">
                      <div className="bg-[#1F4E79] text-white px-4 py-2 flex justify-between text-sm font-semibold">
                        <span>Receipts</span><span>Amount (₹)</span>
                      </div>
                      <div className="px-4 py-3 space-y-1.5 text-sm">
                        <div className="font-semibold text-slate-800">Opening Balance</div>
                        <Row label="CASH-IN-HAND" value={fsData.opening.cash} indent />
                        <Row label="BANK" value={fsData.opening.bank} indent />
                        <div className="font-semibold text-slate-800 pt-2">Receipts For The Period</div>
                        {fsData.receipts.map((r) => <Row key={r.label} label={r.label} value={r.amount} indent />)}
                      </div>
                      <div className="border-t border-slate-300 bg-slate-50 px-4 py-2.5 flex justify-between font-bold text-slate-900">
                        <span>Total</span><span className="font-mono">{fmtINR(fsData.grand_total)}</span>
                      </div>
                    </div>
                    {/* Payments side */}
                    <div className="bg-white">
                      <div className="bg-[#1F4E79] text-white px-4 py-2 flex justify-between text-sm font-semibold">
                        <span>Payments</span><span>Amount (₹)</span>
                      </div>
                      <div className="px-4 py-3 space-y-1.5 text-sm">
                        <div className="font-semibold text-slate-800">Payments For The Period</div>
                        {fsData.payments.length === 0 ? (
                          <div className="text-slate-400 pl-4 text-xs italic">No payments recorded</div>
                        ) : fsData.payments.map((p) => <Row key={p.label} label={p.label} value={p.amount} indent />)}
                        <div className="font-semibold text-slate-800 pt-2">Closing Balance</div>
                        <Row label="CASH-IN-HAND" value={fsData.closing.cash} indent />
                        <Row label="BANK" value={fsData.closing.bank} indent />
                      </div>
                      <div className="border-t border-slate-300 bg-slate-50 px-4 py-2.5 flex justify-between font-bold text-slate-900">
                        <span>Total</span><span className="font-mono">{fmtINR(fsData.grand_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Deposit Return Dialog */}
      <Dialog open={depositReturnDialogOpen} onOpenChange={setDepositReturnDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-emerald-600" />
              Pay Deposit Return
            </DialogTitle>
          </DialogHeader>
          
          {selectedGuestForReturn && (
            <div className="space-y-4 mt-4">
              {/* Guest Info */}
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="font-medium text-emerald-800">{selectedGuestForReturn.name || selectedGuestForReturn.name_of_guest}</p>
                <p className="text-sm text-emerald-600">
                  Room: {selectedGuestForReturn.room || selectedGuestForReturn.flat_no} | 
                  Mobile: {selectedGuestForReturn.mobile || selectedGuestForReturn.mobile_number}
                </p>
                <p className="text-xs text-emerald-500 mt-1">
                  Status: {selectedGuestForReturn.status} | 
                  Deposit Paid: ₹{(selectedGuestForReturn.deposit_paid || 0).toLocaleString()}
                </p>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input 
                  type="date" 
                  value={depositReturnForm.date}
                  onChange={(e) => setDepositReturnForm({...depositReturnForm, date: e.target.value})}
                  data-testid="deposit-return-date"
                />
              </div>

              {/* Category (Read-only) */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Input 
                  value="GUEST DEPOSIT RETURN PAYMENTS" 
                  disabled 
                  className="bg-slate-100"
                />
              </div>

              {/* Description (Read-only) */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={`DEPOSIT RETURN TO ${(selectedGuestForReturn.name || selectedGuestForReturn.name_of_guest || '').toUpperCase()}`}
                  disabled
                  className="bg-slate-100"
                />
              </div>

              {/* Payment Mode */}
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select 
                  value={depositReturnForm.payment_mode} 
                  onValueChange={(value) => setDepositReturnForm({...depositReturnForm, payment_mode: value})}
                >
                  <SelectTrigger data-testid="deposit-return-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={depositReturnForm.amount}
                  onChange={(e) => setDepositReturnForm({...depositReturnForm, amount: parseFloat(e.target.value) || 0})}
                  className="font-mono text-lg"
                  data-testid="deposit-return-amount"
                />
                <p className="text-xs text-slate-500">
                  Net Payable Balance: ₹{Math.abs(selectedGuestForReturn.net_balance || selectedGuestForReturn.net_ledger_balance || 0).toLocaleString()}
                </p>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Input 
                  value={depositReturnForm.remarks}
                  onChange={(e) => setDepositReturnForm({...depositReturnForm, remarks: e.target.value.toUpperCase()})}
                  placeholder="ANY ADDITIONAL NOTES..."
                  data-testid="deposit-return-remarks"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setDepositReturnDialogOpen(false)}
                  className="flex-1"
                  disabled={processingReturn}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDepositReturn}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={processingReturn || depositReturnForm.amount <= 0}
                  data-testid="confirm-deposit-return-btn"
                >
                  {processingReturn ? 'Processing...' : `Pay ₹${depositReturnForm.amount.toLocaleString()}`}
                </Button>
              </div>

              {/* Info Note */}
              <p className="text-xs text-slate-500 text-center">
                This will record a payment under &quot;GUEST DEPOSIT RETURN PAYMENTS&quot; category and update in Daily Hisab.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settle Dues Dialog (from Ledger) */}
      <Dialog open={settleDuesDialogOpen} onOpenChange={setSettleDuesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-amber-600" />
              Settle Dues - {settleGuestData?.guest_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Net Balance Display */}
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <div className="text-sm text-slate-500 mb-1">Net Balance</div>
              <div className={`text-2xl font-bold font-mono ${
                (settleGuestData?.net_balance || 0) < 0 ? 'text-rose-600' : 'text-emerald-600'
              }`}>
                {(settleGuestData?.net_balance || 0) < 0 
                  ? `-₹${Math.abs(settleGuestData?.net_balance || 0).toLocaleString()}`
                  : `₹${(settleGuestData?.net_balance || 0).toLocaleString()}`}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {(settleGuestData?.net_balance || 0) < 0 ? 'Receivable' : 'Payable'}
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
              <p className="font-medium">What this does:</p>
              <p className="mt-1">
                {(settleGuestData?.net_balance || 0) < 0 
                  ? `₹${Math.abs(settleGuestData?.net_balance || 0).toLocaleString()} receivable will be written off as uncollectable.`
                  : `₹${(settleGuestData?.net_balance || 0).toLocaleString()} payable will be written off (not returned to guest).`}
              </p>
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Input 
                placeholder="e.g., Guest not reachable"
                value={settleDuesRemarks}
                onChange={(e) => setSettleDuesRemarks(e.target.value)}
                data-testid="settle-remarks-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => { setSettleDuesDialogOpen(false); setSettleGuestData(null); setSettleDuesRemarks(''); }} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSettleDues} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="confirm-settle-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Settle & Write Off
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Dialog (from Ledger - guest owes us) */}
      <Dialog open={receivePaymentDialogOpen} onOpenChange={setReceivePaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="h-5 w-5 text-rose-600" />
              Receive Payment - {receiveGuestData?.name || receiveGuestData?.name_of_guest}
            </DialogTitle>
          </DialogHeader>
          
          {receiveGuestData && (
            <div className="space-y-4 mt-4">
              {/* Guest Info */}
              <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
                <p className="font-medium text-rose-800">{receiveGuestData.name || receiveGuestData.name_of_guest}</p>
                <p className="text-sm text-rose-600">
                  Room: {receiveGuestData.room || receiveGuestData.flat_no} | 
                  Mobile: {receiveGuestData.mobile || receiveGuestData.mobile_number}
                </p>
                <p className="text-xs text-rose-500 mt-1">
                  Outstanding: ₹{Math.abs(receiveGuestData.net_balance || receiveGuestData.net_ledger_balance || 0).toLocaleString()}
                </p>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={receivePaymentForm.date}
                  onChange={(e) => setReceivePaymentForm({...receivePaymentForm, date: e.target.value})}
                  data-testid="receive-payment-date"
                />
              </div>

              {/* Category (Fixed to Rent) */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Input 
                  value="Rent" 
                  disabled 
                  className="bg-slate-100"
                />
              </div>

              {/* Payment Mode */}
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select 
                  value={receivePaymentForm.payment_mode} 
                  onValueChange={(value) => setReceivePaymentForm({...receivePaymentForm, payment_mode: value})}
                >
                  <SelectTrigger data-testid="receive-payment-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={receivePaymentForm.amount}
                  onChange={(e) => setReceivePaymentForm({...receivePaymentForm, amount: parseFloat(e.target.value) || 0})}
                  className="font-mono text-lg"
                  data-testid="receive-payment-amount"
                />
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Input 
                  value={receivePaymentForm.remarks}
                  onChange={(e) => setReceivePaymentForm({...receivePaymentForm, remarks: e.target.value.toUpperCase()})}
                  placeholder="ANY ADDITIONAL NOTES..."
                  data-testid="receive-payment-remarks"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => { setReceivePaymentDialogOpen(false); setReceiveGuestData(null); }}
                  className="flex-1"
                  disabled={processingReceive}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleReceivePayment}
                  className="flex-1 bg-rose-600 hover:bg-rose-700"
                  disabled={processingReceive || receivePaymentForm.amount <= 0}
                  data-testid="confirm-receive-payment-btn"
                >
                  {processingReceive ? 'Processing...' : `Receive ₹${receivePaymentForm.amount.toLocaleString()}`}
                </Button>
              </div>

              {/* Info Note */}
              <p className="text-xs text-slate-500 text-center">
                This will record a rent receipt and update the guest's balance.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
